import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  Skeleton,
  Rating,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getBarbers } from '../../services/api';
import type { Barber } from '../../types';

const BarberShopList: React.FC = () => {
  const [barbers, setBarbers] = useState<Barber[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBarbers();
  }, []);

  const fetchBarbers = async () => {
    try {
      const data = await getBarbers();
      setBarbers(data);
    } catch (error) {
      console.error('Failed to fetch barbers:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
        Choose a Barber Shop
      </Typography>
      
      <Grid container spacing={3}>
        {loading ? (
          // Loading skeletons
          [...Array(4)].map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Skeleton variant="circular" width={80} height={80} />
                <Skeleton variant="text" sx={{ mt: 2 }} />
                <Skeleton variant="text" width="60%" />
                <Skeleton variant="rectangular" height={60} sx={{ mt: 2 }} />
              </Paper>
            </Grid>
          ))
        ) : (
          barbers.map((barber) => (
            <Grid item xs={12} sm={6} md={4} key={barber.id}>
              <Paper
                sx={{
                  p: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 2,
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar
                    src={barber.avatarUrl}
                    sx={{ width: 80, height: 80, mr: 2 }}
                  />
                  <Box>
                    <Typography variant="h6">{barber.name}</Typography>
                    <Rating 
                      value={barber.rating ?? 0} 
                      readOnly 
                      size="small" 
                    />
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ mb: 2, flexGrow: 1 }}>
                  {barber.bio || "Visit our shop to see what we have to offer!"}
                </Typography>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(`/shop/${barber.id}`)}
                  sx={{
                    mt: 'auto',
                    backgroundColor: 'primary.main',
                    color: 'text.light',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  }}
                >
                  Visit Shop
                </Button>
              </Paper>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
};

export default BarberShopList; 